import { isRecord } from '@/utils/objects';
import type { JsonValue, NamedBitmapFilter, NotFilter, SerializedLegacyMessageFilter } from './wire-types';
import { messageChannelOf } from '../rows/message-channels';
import { DEFAULT_OPT_OUT_CHANNEL, type OptOutChannel } from '../rows/opt-out-channel';

/**
 * The `named_bitmap` leaf codec. A message/event condition packs its whole
 * meaning — event type prefix, message id/name, `_h` human suffix — into one
 * base64 name in the message-value grammar; an opt-out condition carries its
 * channel entirely in the bitmap type number and the sender phone number as
 * the base64 name. The encoded bytes are a cross-app and backend contract
 * (services core/events/index.go indexes these names verbatim), so both
 * directions here must round-trip byte-identically.
 */

export const MESSAGE_TYPE = 3;
// SMS opt-out bitmap index type (db.IndexTypeSMSOptout). WhatsApp opt-outs are
// maintained in a separate bitmap (db.IndexTypeWhatsAppOptout).
export const OPTOUT_TYPE = 22;
export const WHATSAPP_OPTOUT_TYPE = 33;

// The channel is carried entirely by this type number — the opt-out filter has
// no separate channel field on the wire.
export function optOutBitmapType(channel: OptOutChannel): number {
  return channel === 'whatsapp' ? WHATSAPP_OPTOUT_TYPE : OPTOUT_TYPE;
}

export function isOptOutBitmapType(type: number): boolean {
  return type === OPTOUT_TYPE || type === WHATSAPP_OPTOUT_TYPE;
}

export function optOutChannelForBitmapType(type: number): OptOutChannel {
  return type === WHATSAPP_OPTOUT_TYPE ? 'whatsapp' : 'sms';
}

// https://github.com/customerio/services/blob/master/src/customerio/core/events/types/shorten.go#L89
export const TYPE_MAP = Object.freeze({
  page: 'p',
  event: 'e',
  attribute_change: 'a',
  failed_attribute_change: 'fac',
  stripe_event: 'stp',
  drafted_email: 'dfte',
  failed_email: 'fe',
  dropped_email: 'dpe',
  sent_email: 'se',
  spammed_email: 'spme',
  bounced_email: 'be',
  delivered_email: 'de',
  triggered_email: 'de',
  opened_email: 'oe',
  clicked_email: 'ce',
  converted_email: 'cve',
  unsubscribed_email: 'ue',
  attempted_email: 'ae',
  undeliverable_email: 'ude',
  deferred_email: 'dfre',
  device_change: 'd',

  attempted_action: 'aa',
  drafted_action: 'da',
  sent_action: 'sa',
  delivered_action: 'dva',
  bounced_action: 'ba',
  failed_action: 'fa',
  converted_action: 'ca',
  undeliverable_action: 'uda',
  // only makes sense for push for now
  opened_action: 'oa',
  clicked_action: 'cla',
  dropped_action: 'dpa',
  spammed_action: 'spma',
  replied_action: 'ra',

  clicked_content: 'cc',
  viewed_content: 'vc',
  converted_content: 'cvc',
});

// The picker value for "any email": a bare event-type name with no message
// qualifier at all on the wire.
const ANY_EMAIL = '*';

// Matches event names of the format `se-email-campaign_10` /
// `sa-webhook-campaign_5` — any message of a given action type in a specific
// campaign. Capture groups: the message type (per TYPE_MAP), the action type,
// the campaign id.
const ANY_IN_PARENT_REGEX = /([^-]+)-(.+)-campaign_(.+)/;

// Needed to account for utf-8 chars when decoding base64.
function base64ToBytes(value: string): string {
  const binString = atob(value);
  return new TextDecoder('utf-8').decode(Uint8Array.from(binString, (character) => character.charCodeAt(0)));
}

// Needed to account for utf-8 chars when encoding to base64.
function bytesToBase64(value: string): string {
  const encoded = new TextEncoder().encode(value);
  const binString = Array.from(encoded, (byte) => String.fromCodePoint(byte)).join('');
  return btoa(binString);
}

export { base64ToBytes as decodeBitmapName, bytesToBase64 as encodeBitmapName };

function namedBitmapLeafOf(json: unknown): NamedBitmapFilter | null {
  if (!isRecord(json) || !isRecord(json.named_bitmap)) {
    return null;
  }
  const { type, name } = json.named_bitmap;
  if (typeof type !== 'number' || typeof name !== 'string') {
    return null;
  }
  return { named_bitmap: { type, name } };
}

/**
 * Unwraps `{ named_bitmap: {...} }` or `{ not: { named_bitmap: {...} } }` into
 * the leaf plus negation flag, or null for any other shape. Shared by every
 * plugin that claims the `named_bitmap` leaf key (opt-out, message, event) —
 * they gate on disjoint bitmap types / decoded event types after unwrapping.
 */
function unwrapNamedBitmapNode(json: unknown): { leaf: NamedBitmapFilter; negated: boolean } | null {
  const direct = namedBitmapLeafOf(json);
  if (direct != null) {
    return { leaf: direct, negated: false };
  }
  const negated = isRecord(json) ? namedBitmapLeafOf(json.not) : null;
  if (negated != null) {
    return { leaf: negated, negated: true };
  }
  return null;
}

/**
 * Unwraps and decodes a type-3 (message) `named_bitmap` node into the legacy
 * message-condition envelope, or null when it isn't one this codec can speak:
 * the wrong bitmap type (opt-out types belong to the opt-out plugin) or an
 * undecodable name (bad base64, unknown type prefix). The message and event
 * plugins gate on the decoded event type after this shared step.
 */
export function decodeMessageBitmapNode(json: unknown): SerializedLegacyMessageFilter | null {
  const unwrapped = unwrapNamedBitmapNode(json);
  if (unwrapped?.leaf.named_bitmap.type !== MESSAGE_TYPE) {
    return null;
  }
  try {
    return namedBitmapToCondition(unwrapped.leaf, unwrapped.negated);
  } catch {
    return null;
  }
}

/**
 * Unwraps an opt-out-typed (22/33) `named_bitmap` node, or null for any other
 * shape or bitmap type.
 */
export function unwrapOptOutBitmapNode(json: unknown): { leaf: NamedBitmapFilter; negated: boolean } | null {
  const unwrapped = unwrapNamedBitmapNode(json);
  if (unwrapped == null || !isOptOutBitmapType(unwrapped.leaf.named_bitmap.type)) {
    return null;
  }
  return unwrapped;
}

/**
 * Decodes a `named_bitmap` leaf into the message-condition envelope the UI
 * edits. Throws on names whose decoded type prefix is outside TYPE_MAP —
 * callers claiming wire nodes catch and decline instead.
 */
// fallow-ignore-next-line complexity -- the message-value grammar's branch table; splitting hurts readability
export function namedBitmapToCondition(
  { named_bitmap: filter }: NamedBitmapFilter,
  not = false,
): SerializedLegacyMessageFilter {
  let messageType = base64ToBytes(filter.name);
  const isHuman = messageType.endsWith('_h');
  if (isHuman) {
    messageType = messageType.slice(0, -2);
  }

  let messageName;

  // A dash means the event refers to a specific action type or id.
  if (messageType.includes('-')) {
    const anyInParentMatch = ANY_IN_PARENT_REGEX.exec(messageType);

    if (anyInParentMatch != null) {
      const [, eventType, actionType, campaignId] = anyInParentMatch;
      messageType = eventType ?? '';
      messageName = `${actionType}-campaign_${campaignId}`;
    } else {
      // The message name itself can contain `-` characters.
      const [type, ...rest] = messageType.split('-');

      messageType = type ?? '';
      messageName = rest.join('-');
      // No underscore in the message name (id) means the filter refers to all
      // messages of that type; non-email actions need an `_*` suffix to map to
      // the UI filter format. Channel values that themselves contain an `_`
      // (`in_app`, `urban_airship`) need the exact-match check to tell "all
      // messages on this channel" from a specific message id (FPS-3715).
      if (
        (!messageName.includes('_') || messageChannelOf(messageName) != null) &&
        !['output', 'newsletter'].includes(messageName) &&
        messageType !== TYPE_MAP.event
      ) {
        messageName = `${messageName}_*`;
      }
    }
  } else {
    // No dash means the event refers to all EMAIL actions; the UI spells that
    // as the `*` wildcard.
    messageName = ANY_EMAIL;
  }

  // Map the encoded message type back to the UI event type.
  const uiEventTypeEntry = Object.entries(TYPE_MAP).find(([, value]) => value === messageType);
  if (uiEventTypeEntry == null) {
    throw new Error(`Unable to map named bitmap message type: ${messageType}`);
  }

  const [uiEventType] = uiEventTypeEntry;

  const event: SerializedLegacyMessageFilter['event'] = {
    type: uiEventType,
    name: messageName,
    filters: {},
  };

  if (isHuman) {
    event.human = true;
  }

  return {
    event,
    times: 1,
    within: 0,
    inverse: not,
    type: 'message',
  };
}

function isHumanExistsLeaf(candidate: JsonValue): boolean {
  if (typeof candidate !== 'object' || candidate == null || Array.isArray(candidate)) {
    return false;
  }
  if (candidate.field !== 'prefetched' && candidate.field !== 'machine') {
    return false;
  }
  return (candidate.operator === 'exists' && candidate.inverse === true) || candidate.operator === '!exists';
}

// The `_h` human suffix can also arrive spelled as a prefetched/machine
// exists-filter inside the event's data filters; the encoder folds every
// spelling into the suffix.
function hasHumanFilters(filters: SerializedLegacyMessageFilter['event']['filters']): boolean {
  if (filters == null) {
    return false;
  }
  const group = Array.isArray(filters.and) ? filters.and : Array.isArray(filters.or) ? filters.or : [filters];
  return group.some(isHumanExistsLeaf);
}

/**
 * Encodes the message-condition envelope back into a `named_bitmap` leaf,
 * emitting its own `{ not }` wrapper from `inverse`.
 */
export function conditionToNamedBitmap(condition: SerializedLegacyMessageFilter): NamedBitmapFilter | NotFilter {
  const messageType = Object.entries<string>(TYPE_MAP).find(([key]) => key === condition.event.type);

  if (messageType == null) {
    throw new Error('unknown message type');
  }

  let [, name] = messageType;
  const eventName = condition.event.name;

  const isHuman =
    eventName.endsWith('_h') || hasHumanFilters(condition.event.filters) || condition.event.human === true;

  if (condition.event.name !== ANY_EMAIL) {
    name = `${name}-${eventName.replace('_*', '')}`;
  }

  if (isHuman && !name.endsWith('_h')) {
    name = `${name}_h`;
  }

  const filter = createNamedBitmapFilter(MESSAGE_TYPE, name);

  if (condition.inverse) {
    return { not: filter };
  }

  return filter;
}

export function createOptOutBitmapFilter(
  name: string,
  channel: OptOutChannel = DEFAULT_OPT_OUT_CHANNEL,
): NamedBitmapFilter {
  return createNamedBitmapFilter(optOutBitmapType(channel), name);
}

function createNamedBitmapFilter(type: number, name: string): NamedBitmapFilter {
  return {
    named_bitmap: {
      type,
      // The backend wants this base64 encoded.
      name: bytesToBase64(name),
    },
  };
}
